import { collection, doc, getDocs, setDoc } from 'firebase/firestore'
import { db, getFirebaseAuth } from './firebase'

// --- Types ---

export type NcCachedTeam = { teamName: string; logoUrl?: string }

export type NcCachedDivision = { divName: string; teams: Record<string, NcCachedTeam> }

// Keep this cache document shape in sync with firestore.rules (/ncCache/{orgKey}/competitions/{competitionKey}).
export type NcCachedCompetition = {
	competitionId: number
	compName: string
	year: number
	updatedAt: number
	divisions: Record<string, NcCachedDivision>
}

export type NcCachedCompetitionWithKey = NcCachedCompetition & {
	competitionKey: string
}

export type NcSetupSelection = {
	orgKey: string
	orgName: string
	competitionKey: string
	competitionId: number
	compName: string
	divId: number
	divName: string
	teamId: number
	teamName: string
	logoUrl?: string
}

// Keep this org document shape in sync with firestore.rules (/ncCache/{orgKey}).
type NcCacheTarget = Pick<NcSetupSelection, 'orgKey' | 'orgName' | 'competitionKey'>

// --- TTL ---

const BASE_TTL_MS = 90 * 24 * 60 * 60 * 1000
const JITTER_MS = 3 * 24 * 60 * 60 * 1000

function simpleHash(str: string): number {
	let hash = 0
	for (let i = 0; i < str.length; i++) {
		hash = ((hash << 5) - hash + str.charCodeAt(i)) | 0
	}
	return hash
}

export function isCacheFresh(updatedAt: number, userId: string, competitionKey: string): boolean {
	const jitter = (simpleHash(userId + competitionKey) % (JITTER_MS * 2 + 1)) - JITTER_MS
	const ttl = BASE_TTL_MS + jitter
	return Date.now() - updatedAt < ttl
}

// --- Year parsing ---

export function parseYearFromCompName(compName: string): number | null {
	const match = compName.match(/^(20\d{2})\b/)
	return match ? Number(match[1]) : null
}

// --- Firestore read/write ---

export async function getCachedCompetitions(orgKey: string): Promise<NcCachedCompetitionWithKey[]> {
	const colRef = collection(db, 'ncCache', orgKey, 'competitions')
	const snapshot = await getDocs(colRef)
	console.log('getCachedCompetitions:', {
		orgKey,
		competitionCount: snapshot.size,
	})
	return snapshot.docs.map(d => ({
		...(d.data() as NcCachedCompetition),
		competitionKey: d.id,
	}))
}

export async function contributeToCache(
	target: NcCacheTarget,
	competitionData: NcCachedCompetition,
): Promise<void> {
	try {
		if (!target.orgKey || !target.competitionKey) {
			console.warn('contributeToCache skipped: missing cache identifiers.', target)
			return
		}

		console.log('contributeToCache: writing cache entry', {
			orgKey: target.orgKey,
			competitionKey: target.competitionKey,
			year: competitionData.year,
			divisionCount: Object.keys(competitionData.divisions).length,
		})

		if (target.orgName) {
			const orgRef = doc(db, 'ncCache', target.orgKey)
			// firestore.rules only allows { orgName } on the org cache doc.
			await setDoc(orgRef, { orgName: target.orgName }, { merge: true })
		}

		const compRef = doc(db, 'ncCache', target.orgKey, 'competitions', target.competitionKey)
		// firestore.rules validates this competition doc's keys, year range, and updatedAt ceiling.
		await setDoc(compRef, competitionData, { merge: true })
		console.log('contributeToCache: wrote competition cache entry', {
			orgKey: target.orgKey,
			competitionKey: target.competitionKey,
		})
	} catch (err) {
		console.error('contributeToCache failed (non-blocking):', err, {
			orgKey: target.orgKey,
			competitionKey: target.competitionKey,
			year: competitionData.year,
		})
	}
}

// --- Conversion ---

export function selectionToConnectorTeam(selection: NcSetupSelection): ConnectorTeam {
	return {
		compName: selection.compName,
		competitionId: selection.competitionId,
		divId: selection.divId,
		divName: selection.divName,
		id: selection.teamId,
		orgName: selection.orgName,
		teamName: selection.teamName,
	}
}

// --- Helpers ---

export function getCurrentUserId(): string {
	return getFirebaseAuth().currentUser?.uid ?? 'anonymous'
}
